import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import {
  IWebsiteBuilderSectionBackground,
  TWebsiteBuilderSection,
  WEBSITE_BUILDER_SECTION_BACKGROUND_DARKEN_CLASS_NAME,
  WEBSITE_BUILDER_SECTION_BACKGROUND_MEDIA_CLASS_NAME,
  WEBSITE_BUILDER_SECTION_BORDER_COLOR,
  isWebsiteBuilderSectionBackgroundVideo,
  websiteBuilderGenerateSectionBounds,
  websiteBuilderGenerateSectionDesignStyles,
} from "../../../../nodes/section/websiteBuilderSection";
import { useWebsiteBuilderEditorContext } from "../../../../websiteBuilderEditorContext";
import classNames from "classnames";
import { IWebsiteBuilderRoot } from "../../../../nodes/root/websiteBuilderRoot";
import { useActivePageDomNodeRefMapRegistration } from "../../../../hooks/activePageDomNodeRefMap/useActivePageDomNodeRefMapRegistration";
import { useDrop, XYCoord } from "react-dnd";
import { isWebsiteBuilderDraggableNode, TWebsiteBuilderDraggableNode, TWebsiteBuilderDraggableNodeDropItemType } from "../../../../nodes/websiteBuilderNode";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  websiteBuilderSelectNode,
  websiteBuilderUpdateSelectedNode,
} from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { IWebsiteBuilderDropSectionEntityInFreeformDomainRect, IWebsiteBuilderFreeformParentContext, websiteBuilderDropSectionEntityInFreeform } from "../../../../nodes/websiteBuilderFreeformParentNode";
import { SectionHighlightTitle } from "./SectionHighlightTitle";
import { SectionToolbar } from "./SectionToolbar";
import { SectionAddSectionButtons } from "./SectionAddSectionButtons";
import {
  IUseSectionViewResizeArgs,
  useSectionViewResize,
} from "../../hooks/section/useSectionViewResize/useSectionViewResize";
import { Property } from "csstype";
import {
  IUseSectionViewContextMenuArgs,
  useSectionViewContextMenu,
} from "../../hooks/section/useSectionViewContextMenu/useSectionViewContextMenu";
import { IWebsiteBuilderDesignSpaceBoundaries } from "../../../../WebsiteBuilderEditor";
import { useWebsiteBuilderDndManager } from "../../../../hooks/dndManager/useWebsiteBuilderDndManager";
import {
  IUseSectionViewSettingsArgs,
  useSectionViewSettings,
} from "../../hooks/section/useSectionViewSettings/useSectionViewSettings";

interface IWebsiteBuilderSectionViewProps {
  root: IWebsiteBuilderRoot;
  node: TWebsiteBuilderSection;
  children: React.ReactNode;
}

interface IWebsiteBuilderSectionViewDropState {
  sectionIsDropTarget: boolean;
}

type TWebsiteBuilderSectionViewDropItemBoundsCheck = "none" | "out-left" | "inside" | "out-right" | "inside-top" | "inside-bottom";

export type TWebsiteBuilderSectionViewHighlight = "none" | "weak" | "strong";

const ACCEPTED_SECTION_DROP_ITEMS: TWebsiteBuilderDraggableNodeDropItemType[] = ["website-builder-element", "website-builder-container", "website-builder-section"];

function SectionBackgroundVideo(props: {
  background: IWebsiteBuilderSectionBackground;
}) {
  const { background } = props;
  const style: CSSProperties = {
    objectFit: "cover",
    objectPosition: `${background.xPercent}% ${background.yPercent}%`,
  };

  return (
    <video
      className={WEBSITE_BUILDER_SECTION_BACKGROUND_MEDIA_CLASS_NAME}
      src={background.src}
      muted
      playsInline
      disablePictureInPicture
      preload="auto"
      aria-hidden="true"
      style={style}
    />
  );
}

export function WebsiteBuilderSectionView(props: IWebsiteBuilderSectionViewProps) {
  const { root, node, children } = props;
  const {
    type: sectionType,
    layout: sectionLayout,
    background: sectionBackground,
  } = node.section;

  const dispatch = useAppDispatch();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const selectedNode = useAppSelector(store => store.websiteBuilderStore.editor.selectedNode);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);

  const sectionHeight = sectionLayout[deviceView].height;

  const selectedNodeSectionId =
    selectedNode?.type === "container" || selectedNode?.type === "element" ? selectedNode.sectionId : null;

  const {
    activePageDomNodeRefMap,
    sectionOperations,
    designSpaceBoundaries,
    scrollContainerRef,
    overlayLinesRef,
  } = useWebsiteBuilderEditorContext();

  const dndManager = useWebsiteBuilderDndManager();

  const activePageDomNodeRefMapRegistration = useActivePageDomNodeRefMapRegistration(node.id, activePageDomNodeRefMap);

  const usesBackgroundVideo = isWebsiteBuilderSectionBackgroundVideo(sectionBackground);
  const designStyles = websiteBuilderGenerateSectionDesignStyles(sectionBackground);

  const {
    sectionSelected,
    sectionResizing,
    sectionSettingsActive,
    sectionContextMenuActive,
    containerChildSelected,
    containerChildMoving,
    elementDescendantSelected,
    elementDescendantMoving,
  } = useMemo(() => {
    let sectionSelected = false;
    let sectionResizing = false;
    let sectionSettingsActive = false;
    let sectionContextMenuActive = false;

    let containerChildSelected = false;
    let containerChildMoving = false;
    
    let elementDescendantSelected = false;
    let elementDescendantMoving = false;
    
    if (selectedNode?.type === "section") {
      sectionSelected = node.id === selectedNode.id;

      if (sectionSelected) {
        const editingModeType = selectedNode.editingMode.type;

        sectionResizing = editingModeType === "section-resize";
        sectionSettingsActive = editingModeType === "section-settings";
        sectionContextMenuActive = editingModeType === "section-context-menu";
      }
    } else if (selectedNode?.type === "container") {
      containerChildSelected = node.id === selectedNodeSectionId;

      if (containerChildSelected) {
        const editingModeType = selectedNode.editingMode.type;

        containerChildMoving = editingModeType === "container-move";
      }
    } else if (selectedNode?.type === "element") {
      elementDescendantSelected = node.id === selectedNodeSectionId;

      if (elementDescendantSelected) {
        const editingModeType = selectedNode.editingMode.type;

        elementDescendantMoving = editingModeType === "element-move";
      }
    }

    return {
      sectionSelected,
      sectionResizing,
      sectionSettingsActive,
      sectionContextMenuActive,
      containerChildSelected,
      containerChildMoving,
      elementDescendantSelected,
      elementDescendantMoving,
    };
  }, [
    selectedNode?.type,
    selectedNode?.id,
    selectedNode?.editingMode?.type,
    selectedNodeSectionId,
    node.id,
  ]);

  const [sectionHovered, setSectionHovered] = useState<boolean>(false);

  const viewResizeArgs: IUseSectionViewResizeArgs = useMemo(
    () => ({
      id: node.id,
      hovered: sectionHovered,
      resizing: sectionResizing,
      height: sectionHeight,
      device: deviceView,
      children: node.children,
    }),
    [node.id, sectionHovered, sectionResizing, sectionHeight, deviceView, node.children],
  );

  const { viewResizeHeight, viewResizeHandle, viewResizeIndicator } = useSectionViewResize(viewResizeArgs);

  const viewSettingsArgs: IUseSectionViewSettingsArgs = useMemo(
    () => ({
      node,
      selected: sectionSelected,
      settingsActive: sectionSettingsActive,
    }),
    [node, sectionSelected, sectionSettingsActive],
  );

  const { viewSettings, handleViewSettingsClick } = useSectionViewSettings(viewSettingsArgs);

  const viewContextMenuArgs: IUseSectionViewContextMenuArgs = useMemo(() => {
    return {
      node,
      root,
      type: sectionType,
      selected: sectionSelected,
      menuActive: sectionContextMenuActive,
      openSettings: handleViewSettingsClick,
    };
  }, [
    node,
    root,
    sectionType,
    sectionSelected,
    sectionContextMenuActive,
    handleViewSettingsClick,
  ]);

  const { viewContextMenu, handleViewOpenContextMenu } = useSectionViewContextMenu(viewContextMenuArgs);

  const [dropItemBoundsCheck, setDropItemBoundsCheck] = useState<TWebsiteBuilderSectionViewDropItemBoundsCheck>("none");

  const getDropItemBoundsCheck = useCallback((
    item: TWebsiteBuilderDraggableNode,
    node: TWebsiteBuilderSection,
    clientOffset: XYCoord,
    sectionBoundingRect: DOMRect,
    designSpaceBoundaries: IWebsiteBuilderDesignSpaceBoundaries,
  ) => {
    if (item.type === "section") {
      if (!clientOffset || !sectionBoundingRect) {
        return "none";
      }
      
      if (node.section.type === "header") {
        return "inside-bottom";
      } else if (node.section.type === "footer") {
        return "inside-top";
      }

      if (clientOffset.y < sectionBoundingRect.top + sectionBoundingRect.height / 2) {
        return "inside-top";
      } else {
        return "inside-bottom";
      }
    } else {
      if (!clientOffset || !sectionBoundingRect || !designSpaceBoundaries) {
        return "none";
      } else if (clientOffset.x <= sectionBoundingRect.left + designSpaceBoundaries.leftX) {
        return "out-left";
      } else if (clientOffset.x >= sectionBoundingRect.left + designSpaceBoundaries.rightX) {
        return "out-right";
      } else {
        return "inside";
      }
    }
  }, []);

  const sectionIsMoveTarget =
    (
      selectedNode?.editingMode?.type === "element-move" ||
      selectedNode?.editingMode?.type === "container-move"
    ) &&
    selectedNode.editingMode.sectionMoveTargetId === node.id;


  const [{ sectionIsDropTarget }, drop] = useDrop<TWebsiteBuilderDraggableNode, unknown, IWebsiteBuilderSectionViewDropState>(
    () => ({
      accept: ACCEPTED_SECTION_DROP_ITEMS,
      hover: (item, monitor) => {
        const sectionRef = activePageDomNodeRefMap.current.get(node.id);

        if (!sectionRef) {
          setDropItemBoundsCheck("none");
          return;
        }

        const clientOffset = monitor.getClientOffset();
        const sectionBoundingRect = sectionRef.getBoundingClientRect();

        const updatedDropItemBoundsCheck = getDropItemBoundsCheck(
          item,
          node,
          clientOffset,
          sectionBoundingRect,
          designSpaceBoundaries,
        );

        setDropItemBoundsCheck(updatedDropItemBoundsCheck);
      },
      drop: (item, monitor) => {
        if (monitor.didDrop()) {
          return;
        }

        const sectionRef = activePageDomNodeRefMap.current.get(node.id);

        if (!sectionRef) {
          return;
        }

        const clientOffset = monitor.getClientOffset();

        const sectionBoundingRect = sectionRef.getBoundingClientRect();

        const boundsCheck = getDropItemBoundsCheck(
          item,
          node,
          clientOffset,
          sectionBoundingRect,
          designSpaceBoundaries,
        );

        if (item.type === "section") {
          if (boundsCheck !== "inside-top" && boundsCheck !== "inside-bottom") {
            return;
          }

          const direction = boundsCheck === "inside-top" ? "up" : "down";

          sectionOperations.addSection(item, root, node, direction, activePageId, deviceView, activePageDomNodeRefMap, scrollContainerRef);
        } else {
          if (boundsCheck !== "inside") {
            return;
          }

          const domainRect: IWebsiteBuilderDropSectionEntityInFreeformDomainRect = {
            left: sectionBoundingRect.left + designSpaceBoundaries.leftX,
            top: sectionBoundingRect.top,
            width: designSpaceBoundaries.width,
            height: sectionBoundingRect.height,
          };
          
          const parentContext: IWebsiteBuilderFreeformParentContext = {
            node,
            bounds: {
              desktop: websiteBuilderGenerateSectionBounds(node.section.layout, "desktop"),
              tablet: websiteBuilderGenerateSectionBounds(node.section.layout, "tablet"),
              mobile: websiteBuilderGenerateSectionBounds(node.section.layout, "mobile"),
            },
            ancestry: {
              sectionId: node.id,
              containerId: null,
              gridCellId: null,
              tabId: null,
            },
          };

          websiteBuilderDropSectionEntityInFreeform(
            item,
            parentContext,
            domainRect,
            clientOffset,
            activePageId,
            deviceView,
            overlayLinesRef,
            dispatch,
          );
        }
      },
      canDrop: item => isWebsiteBuilderDraggableNode(item),
      collect: (monitor): IWebsiteBuilderSectionViewDropState => {
        return {
          sectionIsDropTarget: monitor.canDrop() && monitor.isOver(),
        };
      },
    }),
    [
      node,
      designSpaceBoundaries,
      root,
      activePageId,
      deviceView,
      sectionOperations.addSection,
      getDropItemBoundsCheck,
    ],
  );

  useEffect(() => {
    if (!sectionIsDropTarget) {
      setDropItemBoundsCheck("none");
    }
  }, [sectionIsDropTarget]);

  useEffect(() => {
    return () => {
      if (elementDescendantMoving || containerChildMoving) {
        setSectionHovered(false);
      }
    };
  }, [elementDescendantMoving, containerChildMoving]);

  function getSectionHighlight(): TWebsiteBuilderSectionViewHighlight {
    const draggingDraggableNode = dndManager.isDragging && isWebsiteBuilderDraggableNode(dndManager.item);
    
    const strongHighlight = sectionSelected || sectionIsMoveTarget || sectionIsDropTarget;

    const weakHighlight =
      elementDescendantSelected ||
      containerChildSelected ||
      sectionHovered ||
      draggingDraggableNode ||
      selectedNode?.editingMode?.type === "element-move" ||
      selectedNode?.editingMode?.type === "container-move";

    if (strongHighlight) {
      return "strong";
    } else if (weakHighlight) {
      return "weak";
    } else {
      return "none";
    }
  }

  const sectionHighlight = getSectionHighlight();

  function getSectionBackgroundImage(): Property.BackgroundImage {
    const backgroundImages: string[] = [];
    
    const outOfBoundsColor = "#EAECF0";
    const insideBoundsColor = "#D1FADF";

    const isHeader = sectionType === "header";
    const isFooter = sectionType === "footer";

    if (isHeader || isFooter) {
      backgroundImages.push(
        `linear-gradient(${isHeader ? "to top" : "to bottom"},
          ${WEBSITE_BUILDER_SECTION_BORDER_COLOR} 1px,
          transparent 1px
        )`,
      );
    }
    
    if (dropItemBoundsCheck === "out-left") {
      backgroundImages.push(
        `linear-gradient(
          to right,
          ${outOfBoundsColor} ${designSpaceBoundaries.leftX}px,
          transparent ${designSpaceBoundaries.leftX}px
        )`);
    } else if (dropItemBoundsCheck === "out-right") {
      const rightXWithOffset = designSpaceBoundaries.rightX + 1;

      backgroundImages.push(
        `linear-gradient(to right,
          transparent ${rightXWithOffset}px,
          ${outOfBoundsColor} ${rightXWithOffset}px
        )`,
      );
    } else if (dropItemBoundsCheck === "inside-top") {
      backgroundImages.push(
        `linear-gradient(
          to bottom,
          ${insideBoundsColor} 0px,
          transparent 50px
        )`);
    } else if (dropItemBoundsCheck === "inside-bottom") {
      backgroundImages.push(
        `linear-gradient(
          to top,
          ${insideBoundsColor} 0px,
          transparent 50px
        )`);
    }

    if (sectionBackground.darken && !usesBackgroundVideo) {
      backgroundImages.push(`linear-gradient(0deg, rgba(0, 0, 0, ${sectionBackground.darkenPercent / 100}))`);
    }

    if (sectionBackground.src && !usesBackgroundVideo) {
      backgroundImages.push(`url(${sectionBackground.src})`);
    }

    return backgroundImages.join(", ");
  }

  return (
    <div
      ref={el => {
        drop(el);
        activePageDomNodeRefMapRegistration(el);
      }}
      className={classNames(designStyles.className, {
        "section-highlight-weak": sectionHighlight === "weak",
        "section-highlight-strong": sectionHighlight === "strong",
      })}
      style={{
        ...designStyles.styleProperties,
        height: viewResizeHeight,
        backgroundImage: getSectionBackgroundImage(),
      }}
      onContextMenu={e => {
        e.stopPropagation();
        e.preventDefault();

        handleViewOpenContextMenu({ x: e.clientX, y: e.clientY });
      }}
      onMouseOver={() => setSectionHovered(true)}
      onMouseOut={e => {
        const mouseOutToTarget = e.relatedTarget as HTMLElement;

        if (!mouseOutToTarget || !e.currentTarget.contains(mouseOutToTarget)) {
          setSectionHovered(false);
        }
      }}
      onPointerDown={e => {
        e.stopPropagation();

        if (!sectionSelected) {
          dispatch(
            websiteBuilderSelectNode({
              selectedNode: {
                type: "section",
                id: node.id,
                editingMode: {
                  type: "none",
                },
              },
            }),
          );
        }

        if (e.button === 0 && sectionContextMenuActive) {
          dispatch(
            websiteBuilderUpdateSelectedNode({
              selectedNodeUpdates: {
                type: "section",
                editingMode: {
                  type: "none",
                },
              },
            }),
          );
        }
      }}
    >
      {usesBackgroundVideo && (
        <div className="website-builder-section-foreground">
          <SectionBackgroundVideo background={sectionBackground} />
          {sectionBackground.darken && (
            <div
              className={WEBSITE_BUILDER_SECTION_BACKGROUND_DARKEN_CLASS_NAME}
              style={{
                backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, ${sectionBackground.darkenPercent / 100}))`,
              }}
            />
          )}
        </div>
      )}
      {children}
      <SectionHighlightTitle highlight={sectionHighlight} node={node} root={root} />
      {!sectionResizing && (
        <SectionToolbar
          node={node}
          root={root}
          hovered={sectionHovered}
          sectionHeight={sectionHeight}
          sectionOperations={sectionOperations}
          handleViewOpenContextMenu={handleViewOpenContextMenu}
        />
      )}
      <SectionAddSectionButtons
        node={node}
        root={root}
        hovered={sectionHovered}
        sectionOperations={sectionOperations}
      />
      {viewResizeHandle}
      {viewResizeIndicator}
      {viewSettings}
      {viewContextMenu}
    </div>
  );
}
